import { LangDisplay, Store, StoreDisplay } from '../types';
import { LANG } from '../config';
import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LocationFilters, PartialLocation, StoresContext } from '../App';

export function debounce(func: Function, timeout = 300) {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            // @ts-ignore
            func.apply(this, args);
        }, timeout);
    };
}

export function getLangVar(s: StoreDisplay, key: keyof LangDisplay) {
    return s.langs[LANG]?.[key] || s.langs['ru']?.[key] || null;
}

const locale = {
    ru: {
        address: 'Адрес',
        district: 'Федеральный округ',
        phone: 'Телефон',
        email: 'Email',
        stock: 'В наличии',
        workHours: 'Время работы',
        website: 'Сайт',
        deliveryDays: 'Дни доставки',
        deliveryComment: 'Примечание к доставке',
    },
};

export const getStoreBalloonOptions = (s: StoreDisplay) => {
    const phone = getLangVar(s, 'phone');
    const email = getLangVar(s, 'email');
    const values = {
        address: getLangVar(s, 'address') || '',
        district: s.federalDistrict,
        workHours: getLangVar(s, 'workHours') || '',
        phone: (phone && `<a href='tel:${phone}'>${phone}</a>`) || '',
        email: (email && `<a href='mailto:${email}'>${email}</a>`) || '',
        website: s.url && `<a href='${s.url}'>${s.url}</a>`,
        stock: !!s.product?.stock && 'да',
        deliveryDays: !!s.product?.stock && s.product?.deliveryDays,
        deliveryComment: !!s.product?.stock && s.product?.comment,
    };
    const rows = Object.entries(values)
        .filter(([_, v]) => v)
        .map(
            ([k, v]) => `
            <div class='map-popup-text'>
              <span>${locale[LANG]?.[k as keyof typeof values] || 'missing label'}: </span>${v}
            </div>
        `
        );
    return `
<div style='overflow: scroll;'>
   <div class='map-popup-desc'>
        <div class='map-popup-cap'>
          <strong>${getLangVar(s, 'name')}</strong>
        </div>
        ${rows.join('')}   
   </div>     
</div>
`;
};

export function useOutsideClick() {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (isOpen) {
            const callback = (e: MouseEvent) => {
                if (!dropdownRef.current?.contains(e.target as Node)) {
                    setIsOpen(false);
                }
            };
            window.addEventListener('click', callback);
            return () => window.removeEventListener('click', callback);
        }
    }, [isOpen]);
    return { isOpen, setIsOpen, dropdownRef };
}

export function useDebouncedSearch() {
    const { setSearch } = useContext(StoresContext);
    return useMemo(
        () =>
            debounce((e: ChangeEvent<HTMLInputElement>) => {
                setSearch(e?.target?.value?.trim()?.toLowerCase() ?? '');
            }),
        [setSearch]
    );
}

export function normalizeLocation(str: string) {
    return str.toLowerCase().replace(/\.|-| |,|область|обл|край|республика|автономный округ/g, '');
}

export function matchDetectedLocation(location: NonNullable<PartialLocation>, stores: Store[]) {
    const newLocation = {
        country: '',
        region: '',
        city: '',
    };
    if (!location.country) {
        return newLocation;
    }
    const normalizedLocMap = {
        country: new Map<string, string>(),
        region: new Map<string, string>(),
        city: new Map<string, string>(),
    };
    const keys = ['country', 'region', 'city'] as (keyof LocationFilters)[];
    for (const s of stores) {
        for (const key of keys) {
            const val = s[key];
            if (val) {
                normalizedLocMap[key].set(normalizeLocation(val), val);
            }
        }
    }
    for (const key of keys) {
        const locVal = location[key];
        if (!locVal) {
            break;
        }
        const map = normalizedLocMap[key];
        if (map.has(locVal)) {
            newLocation[key] = map.get(locVal) as string;
        }
    }

    return newLocation;
}

export const useConfirmLocation = (
    alert: (text: string) => void,
    confirm: (text: string, onConfirm: () => void) => void
) => {
    const { setLocationFilters, locationToSet, setLocationToSet, allStores } = useContext(StoresContext);
    useEffect(() => {
        if (!locationToSet || !allStores.length) {
            return;
        }
        setLocationToSet(null);
        const newLocation = matchDetectedLocation(locationToSet, allStores);
        if (!newLocation.country) {
            alert('Не удалось определить ваше местоположение.\nПожалуйста, уточните его на карте ниже.');
        } else {
            const locStr = Object.values(newLocation)
                .filter((s) => s)
                .join(', ');
            confirm(`Вы находитесь в ${locStr}?`, () => setLocationFilters(newLocation));
        }
    }, [allStores, locationToSet, setLocationFilters, setLocationToSet, alert, confirm]);
};
